import {
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { CostCurvesStrings } from "@/components/tools/cost-curves-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  atPrice,
  type CostTable,
  minimumBy,
  type PriceOutcome,
} from "@/lib/tool-kit/econ-micro/costs";
import { minus } from "@/lib/tool-kit/econ-micro/format";

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const DASH = "—";

function PriceDecision({
  outcome,
  price,
  strings: s,
  fmt,
}: {
  outcome: PriceOutcome;
  price: number;
  strings: CostCurvesStrings;
  fmt: Fmt;
}) {
  const n = (v: number) => minus(fmt(v, { maximumFractionDigits: 2 }));
  const decisions = {
    profit: s.decisionProfit,
    breakEven: s.decisionBreakEven,
    loss: s.decisionLoss,
    shutDown: s.decisionShutDown,
  };
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{t(s.priceHeading, { price: n(price) })}</h3>
      <StatGrid>
        <StatCard emphasis label={s.statBestOutput} value={n(outcome.q)} />
        <StatCard label={s.statRevenue} value={n(outcome.revenue)} />
        <StatCard label={s.statProfit} value={n(outcome.profit)} />
      </StatGrid>
      <p className="text-foreground text-sm">{decisions[outcome.decision]}</p>
      <p className="text-muted-foreground text-sm">{s.ruleNote}</p>
    </div>
  );
}

export function CostView({
  table,
  price,
  strings: s,
  fmt,
}: {
  table: CostTable;
  price: number | null;
  strings: CostCurvesStrings;
  fmt: Fmt;
}) {
  const n = (v: number | null) =>
    v === null ? DASH : minus(fmt(v, { maximumFractionDigits: 2 }));
  const minAtc = minimumBy(table.rows, (r) => r.atc);
  const minAvc = minimumBy(table.rows, (r) => r.avc);
  const outcome = price === null ? null : atPrice(table, price);
  const head = [
    s.columnQ,
    s.columnTc,
    s.columnFc,
    s.columnVc,
    s.columnAfc,
    s.columnAvc,
    s.columnAtc,
    s.columnMc,
    ...(outcome ? [s.columnProfit] : []),
  ];
  const rows = table.rows.map((row, index) => [
    n(row.q),
    n(row.tc),
    n(row.fc),
    n(row.vc),
    n(row.afc),
    n(row.avc),
    n(row.atc),
    n(row.mc),
    ...(outcome ? [n(outcome.profits[index])] : []),
  ]);
  return (
    <div className="space-y-5">
      <StatGrid>
        <StatCard label={s.statFixed} value={n(table.fixedCost)} />
        {minAtc ? (
          <StatCard
            emphasis
            label={t(s.statBreakEven, { q: n(minAtc.q) })}
            value={n(minAtc.atc)}
          />
        ) : null}
        {minAvc ? (
          <StatCard
            emphasis
            label={t(s.statShutdown, { q: n(minAvc.q) })}
            value={n(minAvc.avc)}
          />
        ) : null}
      </StatGrid>
      <p className="text-muted-foreground text-sm">{s.thresholdNote}</p>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{s.tableHeading}</h3>
        <DataTable head={head} rows={rows} />
        <p className="text-muted-foreground text-xs">{s.tableNote}</p>
      </div>
      {outcome && price !== null ? (
        <PriceDecision fmt={fmt} outcome={outcome} price={price} strings={s} />
      ) : null}
    </div>
  );
}
